import {useDispatch, useSelector} from "react-redux";
import {useEffect, useRef, useState} from "react";
import {toast} from "react-toastify";
import {CreateBuildingUsers, GetPublicBuildingById, UpdateBuilding} from "../../../reduxToolkit/Building/BuildingApi";
import {errorMsg} from "../../../Apis/functions";
import {permissionObj} from "../../../Helpers/permission";
import {BUILDING_FORM_MODEL} from "../Model/Model";
import {BuildingFormApp} from "./BuildingFormApp";

/**
 * @Author: karen Yunuen Pozos Arias
 * @JobTitle : Full Stack Developer
 * @Email : kpozos@ibl.mx
 * A modal component for updating buildings with form inputs and validation.
 * The inputs and their handlers come from BuildingFormApp, shared with the create modal.
 * @module UpdateBuildingModalApp
 * @component
 * @param {Object} props - Component props.
 * @param {boolean} props.show - Controls modal visibility.
 * @param {Function} props.onHide - Callback for modal close event.
 * @param {Object} props.building - Row of the list being updated, it fills the
 *        form while the detail of the building arrives.
 * @param {Function} props.setModified - Flags the list to be reloaded after a successful update.
 * @returns {Object} Returns an object containing:
 * - Localization function `t`
 * - UI elements and handlers for form management
 * - The form model `item` and its generic change handler
 */
export const UpdateBuildingModalApp = (props) => {
    const {show, onHide, building, setModified} = props;

    // use hook importer
    const dispatch = useDispatch();
    const DEV = useSelector((state) => state?.CommonsSlice?.development);
    const { permission } = useSelector((state) => state.authenticatioauthennSlice);
    // The custom access section assigns the users of the building
    const canManageUsers = DEV || permission?.includes(permissionObj?.WEB_BUILDING_MANAGE_USERS);

    // Building as it was loaded, the clear button brings the form back to it
    const loadedBuilding = useRef(null);

    // Selection of the Add tab of the residents section, assigned to the
    // building once the update went through
    const [residents, setResidents] = useState([]);

    // Display of the modal, the residents section hides it while its delete
    // confirmation is open so both modals are not stacked
    const [displayModal, setDisplayModal] = useState("");

    const {t, item, setItem, handleChange, clearButton, handleCleanClick, emptyFields, setEmptyFields,
        getListStatus, employeeWorkStations, reset, showOverlay, overlayHiding, handleMouseEnter, handleMouseLeave, handleMapChange
    } = BuildingFormApp({
        show,
        cleanButtonId: 'btn_ubm_clean',
        onReset: () => loadedBuilding.current ?? BUILDING_FORM_MODEL.set(building)
    });

    // Load the building being updated every time the modal is opened
    useEffect(() => {
        if (!show || !building?.id) return;

        loadedBuilding.current = null;
        setResidents([]);
        setItem(BUILDING_FORM_MODEL.set(building));

        dispatch(GetPublicBuildingById(building.id)).then((response) => {
            const {success, data} = response?.payload?.data || {};
            // Keep the row already shown by the list when the detail is not available
            loadedBuilding.current = BUILDING_FORM_MODEL.set(success ? data : building);
            setItem(loadedBuilding.current);
        });
    }, [show, building?.id]);

    /**
     * Assigns the employees picked in the Add tab of the residents section to the
     * building. The service filters the id list, so repeated ids, ids already
     * assigned and unknown ones are ignored.
     * @returns {Promise<boolean>} False only when the assignment failed.
     */
    const assignResidents = async () => {
        if (residents.length <= 0) return true;

        const {payload: {data: {success, code}}} = await dispatch(CreateBuildingUsers({
            id: building.id,
            idList: residents.map((resident) => resident.userId ?? resident.id)
        }));

        if (!success) errorMsg(code);

        return success;
    };

    // a function to handel payload and dispatch api end function
    const handleUpdateBuilding = () => {
        if (!item.isValid()) {
            setEmptyFields(true);
            return;
        }
        setEmptyFields(false);

        dispatch(UpdateBuilding(item.toUpdate())).then(async ({payload: {data: {success, code}}}) => {
            if (!success) {
                errorMsg(code);
                return;
            }
            // The residents picked in the Add tab are assigned after the update
            if (!await assignResidents()) {
                // The building was updated, only the assignment failed
                setModified(true);
                return;
            }

            toast.success(t('has_been_updated_successfully'));
            setModified(true);
            onHide();
        });
    };

    const handleClose = () => {
        reset();
        setResidents([]);
        onHide();
    };

    return {
        t,
        item,
        canManageUsers,
        residents,
        setResidents,
        displayModal,
        setDisplayModal,
        handleChange,
        clearButton,
        handleUpdateBuilding,
        handleCleanClick,
        emptyFields,
        getListStatus,
        employeeWorkStations,
        handleClose,
        showOverlay,
        overlayHiding,
        handleMouseEnter,
        handleMouseLeave,
        handleMapChange
    }

}
